import React, { useState, useMemo, useCallback } from "react";
import {
  Stack,
  Typography,
  Button,
  Popover,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
  HelpOutline as UnclassifiedIcon,
} from "@mui/icons-material";

// ─── Types ──────────────────────────────────────────────────────────

interface CategoryTreeFilterProps {
  categories: string[];
  categoryCounts: Record<string, number>;
  value: string; // '' = all, 'Sin clasificar', or a category path
  onChange: (value: string) => void;
  unclassifiedCount?: number | undefined;
}

interface TreeNode {
  name: string;
  fullPath: string;
  directCount: number;
  totalCount: number;
  children: TreeNode[];
}

// ─── Tree builder ───────────────────────────────────────────────────

function buildTree(
  categories: string[],
  counts: Record<string, number>,
): TreeNode[] {
  const root: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  for (const cat of categories) {
    const parts = cat.split(" / ");
    let currentPath = "";
    let parentChildren = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!;
      currentPath = i === 0 ? part : `${currentPath} / ${part}`;

      let node = nodeMap.get(currentPath);
      if (!node) {
        node = {
          name: part,
          fullPath: currentPath,
          directCount: 0,
          totalCount: 0,
          children: [],
        };
        nodeMap.set(currentPath, node);
        parentChildren.push(node);
      }

      parentChildren = node.children;
    }
  }

  // Assign direct counts (only leaf categories have direct products)
  for (const cat of categories) {
    const node = nodeMap.get(cat);
    if (node) node.directCount = counts[cat] || 0;
  }

  // Calculate total counts (bottom-up)
  function calcTotal(node: TreeNode): number {
    node.totalCount = node.directCount;
    for (const child of node.children) {
      node.totalCount += calcTotal(child);
    }
    return node.totalCount;
  }
  for (const node of root) calcTotal(node);

  // Sort children alphabetically
  function sortTree(nodes: TreeNode[]): void {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortTree(n.children);
  }
  sortTree(root);

  return root;
}

// ─── Component ──────────────────────────────────────────────────────

export default function CategoryTreeFilter({
  categories,
  categoryCounts,
  value,
  onChange,
  unclassifiedCount,
}: CategoryTreeFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState("");

  const tree = useMemo(
    () => buildTree(categories, categoryCounts),
    [categories, categoryCounts],
  );

  const toggleExpand = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (filterValue: string, hasChildren: boolean = false) => {
      onChange(filterValue);
      // If node has children, keep popover open so user can browse the tree
      if (!hasChildren) {
        setAnchorEl(null);
      }
    },
    [onChange],
  );

  // Filter tree by search text
  const filteredTree = useMemo(() => {
    if (!searchText.trim()) return tree;
    const q = searchText.toLowerCase();

    function filterNode(node: TreeNode): TreeNode | null {
      const matches =
        node.name.toLowerCase().includes(q) ||
        node.fullPath.toLowerCase().includes(q);
      const filteredChildren = node.children
        .map(filterNode)
        .filter(Boolean) as TreeNode[];

      if (matches || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    }

    return tree.map(filterNode).filter(Boolean) as TreeNode[];
  }, [tree, searchText]);

  // Auto-expand all when searching
  const expandedSet = useMemo(() => {
    if (searchText.trim()) {
      const allPaths = new Set<string>();
      function collectPaths(nodes: TreeNode[]) {
        for (const n of nodes) {
          if (n.children.length > 0) allPaths.add(n.fullPath);
          collectPaths(n.children);
        }
      }
      collectPaths(filteredTree);
      return allPaths;
    }
    return expanded;
  }, [expanded, searchText, filteredTree]);

  // Auto-expand to show the currently selected value
  useMemo(() => {
    if (value && value !== "Sin clasificar") {
      const parts = value.split(" / ");
      const paths: string[] = [];
      for (let i = 0; i < parts.length - 1; i++) {
        paths.push(parts.slice(0, i + 1).join(" / "));
      }
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const p of paths) next.add(p);
        return next;
      });
    }
  }, [value]);

  const open = Boolean(anchorEl);

  const displayLabel = useMemo(() => {
    if (!value) return "Todas las categorias";
    if (value === "Sin clasificar") return "Sin clasificar";
    const parts = value.split(" / ");
    return parts.length > 2
      ? `.../${parts[parts.length - 2]} / ${parts[parts.length - 1]}`
      : value;
  }, [value]);

  // ─── Render a tree node recursively ─────────────────────────────

  const renderNode = (node: TreeNode, depth: number = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedSet.has(node.fullPath);
    const isSelected = value === node.fullPath;

    return (
      <React.Fragment key={node.fullPath}>
        <ListItemButton
          selected={isSelected}
          onClick={() => {
            // Clicking a parent node: expand/collapse + select (keep popover open)
            if (hasChildren) toggleExpand(node.fullPath);
            handleSelect(node.fullPath, hasChildren);
          }}
          sx={{
            pl: 1 + depth * 2,
            py: 0.25,
            minHeight: 30,
            borderRadius: 0.5,
            mx: 0.5,
            "&.Mui-selected": {
              bgcolor: "rgba(107, 155, 210, 0.12)",
              borderLeft: "2px solid",
              borderColor: "primary.main",
            },
          }}
        >
          {hasChildren ? (
            <ListItemIcon sx={{ minWidth: 22 }}>
              {isExpanded ? (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              )}
            </ListItemIcon>
          ) : (
            <ListItemIcon sx={{ minWidth: 22 }} />
          )}
          <ListItemIcon sx={{ minWidth: 20 }}>
            {hasChildren ? (
              isExpanded ? (
                <FolderOpenIcon sx={{ fontSize: 15, color: "primary.main" }} />
              ) : (
                <FolderIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              )
            ) : null}
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            slotProps={{
              primary: {
                variant: "body2",
                fontWeight: isSelected ? 700 : hasChildren ? 500 : 400,
                fontSize: "0.78rem",
                noWrap: true,
              },
            }}
          />
          {node.totalCount > 0 && (
            <Chip
              label={node.totalCount}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", ml: 0.5 }}
            />
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {node.children.map((child) => renderNode(child, depth + 1))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // ─── Main render ────────────────────────────────────────────────

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FilterIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: "none",
          minWidth: 200,
          justifyContent: "flex-start",
          color: value ? "primary.main" : "text.secondary",
          borderColor: value ? "primary.main" : "divider",
          fontWeight: value ? 600 : 400,
          fontSize: "0.82rem",
        }}
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
          {displayLabel}
        </Typography>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSearchText("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxHeight: 480,
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        {/* Search */}
        <Stack sx={{ p: 1 }}>
          <TextField
            size="small"
            placeholder="Buscar categoria..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoFocus
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
        <Divider />

        {/* Options list */}
        <List dense sx={{ overflow: "auto", flex: 1, py: 0.5 }}>
          {/* "All" option */}
          <ListItemButton
            selected={!value}
            onClick={() => handleSelect("")}
            sx={{
              py: 0.25,
              minHeight: 30,
              borderRadius: 0.5,
              mx: 0.5,
              "&.Mui-selected": {
                bgcolor: "rgba(107, 155, 210, 0.12)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 22 }}>
              <FilterIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            </ListItemIcon>
            <ListItemText
              primary="Todas las categorias"
              slotProps={{
                primary: {
                  variant: "body2",
                  fontWeight: !value ? 700 : 500,
                  fontSize: "0.78rem",
                },
              }}
            />
          </ListItemButton>

          {/* "Unclassified" option */}
          <ListItemButton
            selected={value === "Sin clasificar"}
            onClick={() => handleSelect("Sin clasificar")}
            sx={{
              py: 0.25,
              minHeight: 30,
              borderRadius: 0.5,
              mx: 0.5,
              "&.Mui-selected": {
                bgcolor: "rgba(255, 152, 0, 0.10)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 22 }}>
              <UnclassifiedIcon sx={{ fontSize: 15, color: "warning.main" }} />
            </ListItemIcon>
            <ListItemText
              primary="Sin clasificar"
              slotProps={{
                primary: {
                  variant: "body2",
                  fontWeight: value === "Sin clasificar" ? 700 : 500,
                  fontSize: "0.78rem",
                  color: "warning.main",
                },
              }}
            />
            {unclassifiedCount != null && (
              <Chip
                label={unclassifiedCount.toLocaleString()}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            )}
          </ListItemButton>

          <Divider sx={{ my: 0.5 }} />

          {/* Tree */}
          {filteredTree.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1, textAlign: "center" }}
            >
              Sin categorias
            </Typography>
          ) : (
            filteredTree.map((node) => renderNode(node))
          )}
        </List>
      </Popover>
    </>
  );
}
